import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { toast } from "sonner";
import { BookmarkX, FileText, MessageSquare, ExternalLink } from "lucide-react";

const primaryOf = (c) => (c.professions || []).find((p) => p.priority === "primary");

export default function Shortlist() {
  const [list, setList] = useState(null);
  const [dmTarget, setDmTarget] = useState(null);
  const [dmBody, setDmBody] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/shortlist");
      setList(data);
    } catch { setList([]); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    try {
      await api.post(`/shortlist/${c.user_id}`);
      setList((l) => l.filter((x) => x.user_id !== c.user_id));
      toast.success("Removed from shortlist");
    } catch { toast.error("Failed"); }
  };

  const sendDM = async () => {
    try {
      await api.post("/messages", { to_user_id: dmTarget.user_id, body: dmBody });
      toast.success("Message sent");
      setDmTarget(null); setDmBody("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <span className="text-xs uppercase tracking-[0.2em] text-[#D96C5B] font-semibold">Talent pool</span>
      <h1 className="text-3xl font-bold tracking-tight mt-1">My shortlist</h1>

      <div className="mt-6 space-y-3" data-testid="shortlist-list">
        {list === null && <div className="p-8 text-center text-[#68706B]">Loading…</div>}
        {list?.length === 0 && (
          <div className="p-12 text-center text-[#68706B] bg-white border border-[#E2E4E2]">
            No saved candidates yet. Tap the bookmark on any video in the feed.
          </div>
        )}
        {list?.map((c) => {
          const avatarSrc = c.avatar?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${c.avatar}` : c.avatar;
          const primary = primaryOf(c);
          return (
            <div key={c.user_id} className="bg-white border border-[#E2E4E2] p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`shortlist-item-${c.user_id}`}>
              <div className="w-12 h-12 bg-[#E8ECEB] overflow-hidden flex items-center justify-center font-bold text-[#1E3F33] shrink-0">
                {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" /> : c.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold flex items-center gap-1.5">{c.name} {c.is_verified && <VerifiedBadge />}</div>
                <div className="text-xs text-[#68706B]">{[primary?.name || c.sector, c.location].filter(Boolean).join(" • ")}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" onClick={() => { setDmTarget(c); setDmBody(""); }} className="rounded-none bg-[#1E3F33]" data-testid={`shortlist-dm-${c.user_id}`}><MessageSquare className="w-4 h-4 mr-1" />Message</Button>
                {c.resume_file_id && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.resume_file_id}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="rounded-none"><FileText className="w-4 h-4 mr-1" />Resume</Button>
                  </a>
                )}
                <Link to={`/p/${c.user_id}`}>
                  <Button size="sm" variant="outline" className="rounded-none"><ExternalLink className="w-4 h-4 mr-1" />Profile</Button>
                </Link>
                <Button size="sm" variant="ghost" onClick={() => remove(c)} className="rounded-none text-[#D96C5B]" data-testid={`shortlist-remove-${c.user_id}`}><BookmarkX className="w-4 h-4" /></Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dmTarget} onOpenChange={(o) => !o && setDmTarget(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Message {dmTarget?.name}</DialogTitle></DialogHeader>
          <Textarea value={dmBody} onChange={(e) => setDmBody(e.target.value)} placeholder="Write a short professional message…" className="rounded-none min-h-32" data-testid="shortlist-dm-body" />
          <DialogFooter>
            <Button className="rounded-none bg-[#1E3F33]" onClick={sendDM} data-testid="shortlist-dm-send">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
